import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, Image as ImageIcon, Loader2 } from "lucide-react";

// Scelta semplificata "estetica" del pannello per il configuratore pubblico (torri.solar).
// L'utente sceglie il look; il prodotto TORRI corrispondente viene impostato automaticamente.
const AESTHETICS = [
  { key: "tradizionale", label: "Tradizionale", desc: "Look classico", match: (c) => /450/.test(c) && !/total\s*black/i.test(c) && !/eco/i.test(c) },
  { key: "total_black", label: "Total Black", desc: "Estetica full black elegante", match: (c) => /total\s*black/i.test(c) },
  { key: "ecored", label: "EcoRed", desc: "Finitura rossa integrata", match: (c) => /eco[-\s]?red/i.test(c) },
];

export function AestheticPanelPicker({ selfToken, currentModule, onChanged, className = "" }) {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/public/vetrina").then((r) => {
      const slot = (r.data.slots || []).find((s) => s.slot === "pannello");
      setPanels(slot?.items || []);
    }).catch(() => setPanels([])).finally(() => setLoading(false));
  }, []);

  const cards = useMemo(
    () => AESTHETICS.map((a) => ({ ...a, item: panels.find((it) => a.match(String(it.codice || ""))) })).filter((a) => a.item),
    [panels]
  );

  const curCode = currentModule?.chosen ? String(currentModule?.model || currentModule?.codice || "") : "";

  const choose = async (codice) => {
    setSaving(codice);
    try {
      const { data } = await api.post(`/public/self/${selfToken}/listino-panel`, { codice });
      toast.success("Pannello selezionato");
      onChanged?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(""); }
  };

  if (loading) return <div className={`flex items-center justify-center h-24 ${className}`}><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  if (cards.length === 0) return (
    <div className={`rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-400 ${className}`} data-testid="aesthetic-panel-empty">
      Al momento non riusciamo a caricare i modelli. Prosegui pure: un nostro tecnico definirà il pannello in consulenza.
    </div>
  );

  return (
    <div className={className} data-testid="aesthetic-panel-picker">
      <div className="text-sm font-semibold mb-1">Scegli l'estetica del pannello</div>
      <p className="text-xs text-muted-foreground mb-3">Tutti i modelli sono pannelli TORRI.solar Made in Italy: scegli semplicemente il look che preferisci.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map((a) => {
          const selected = curCode && String(a.item.codice) === curCode;
          return (
            <button key={a.key} type="button" onClick={() => choose(a.item.codice)} disabled={!!saving}
              className={`relative text-left rounded-xl border-2 p-3 transition-colors disabled:opacity-60 ${selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
              data-testid={`aesthetic-${a.key}`}>
              {selected && <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Check className="h-4 w-4" /></span>}
              <div className="aspect-square w-full rounded-lg overflow-hidden border border-border bg-white flex items-center justify-center mb-2">
                {(a.item.immagine_listino || a.item.immagine_url) ? <img src={a.item.immagine_listino || a.item.immagine_url} alt={a.label} className="w-full h-full object-contain" /> : <ImageIcon className="h-8 w-8 text-muted-foreground/40" />}
              </div>
              <div className="font-heading font-bold">{a.label}</div>
              <div className="text-[11px] text-muted-foreground">{a.desc}</div>
              {saving === a.item.codice && <div className="mt-1 text-[11px] text-primary flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Seleziono…</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
